import cuid from "cuid";
import dayjs from "dayjs";
import { FieldArray, Formik } from "formik";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdAdd,
  MdOutlineBadge,
  MdOutlineCake,
  MdOutlineCall,
  MdOutlineDelete,
  MdOutlineEmail,
  MdOutlineLocationOn,
  MdOutlineMoney,
  MdOutlinePerson,
} from "react-icons/md";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  FAMILY_SITUATION,
  GENDER,
  MOBILE_MEANS,
  PROFILE_LIST,
  PROPERTY_OWNERSHIP,
  RELATIONSHIP,
  SITUATION_PROFESSIONAL,
} from "utils/constants";
import {
  bindDateInputProps,
  bindInputProps,
  bindTextFieldInputProps,
} from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import DuplicateCheck from "../Components/DuplicateCheck";
import validation from "./validation";

const RELATIONSHIPS = [
  { value: RELATIONSHIP.SPOUSE, label: "Conjoint(e)" },
  { value: RELATIONSHIP.CHILD, label: "Enfant" },
];

const PROFESSIONAL_SECTORES = [
  { value: SITUATION_PROFESSIONAL.INDEPENDENT, label: "Sans" },
  { value: SITUATION_PROFESSIONAL.SALARIED_PUBLIC_SECTOR, label: "Public" },
  { value: SITUATION_PROFESSIONAL.SALARIED_PRIVATE_SECTOR, label: "Privé" },
];

const PROFESSIONS = [
  { value: PROFILE_LIST.UNEMPLOYED, label: "Sans Profession" },
  { value: PROFILE_LIST.EMPLOYEE, label: "Employé/ouvrier" },
  { value: PROFILE_LIST.MIDDLE_MANAGER, label: "Cadre moyen" },
  { value: PROFILE_LIST.SENIOR_MANAGER, label: "Cadre supérieur" },
  { value: PROFILE_LIST.CEO, label: "Cadre dirigeant" },
];

const MOBILE_MEANS_LIST = [
  { value: MOBILE_MEANS.NO_TRANSPORT, label: "Sans locomotion" },
  {
    value: MOBILE_MEANS.ELECTRIC_CYCLE,
    label: "Vélo ou trottinette électrique",
  },
  { value: MOBILE_MEANS.HYBRID_CAR, label: "Voiture Hybride" },
  { value: MOBILE_MEANS.THERMICAL_CAR, label: "Voiture Thermique" },
  { value: MOBILE_MEANS.ELECTRIC_CAR, label: "Voiture 100% électrique" },
];

const View = ({
  onSubmit,
  initialValues,
  nextLoading,
  getRecommendation,
  onDuplicateClick,
}) => {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  return (
    <Formik
      validateOnMount={true}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      )}
    >
      {({ values, handleSubmit, setFieldValue, isValid, ...formProps }) => {
        const recommendation = getRecommendation(values.members.length);
        return (
          <div className="contact-form" key="form_area">
            <div className="contact-form__content mb-16">
              <Form
                title="Qualification du Prospect"
                description="Analyse de la composition du foyer pour recommander la meilleure formule"
              >
                <div className="contact-stepper" key="ADHERENT">
                  <div className="contact-stepper__header">
                    <div className="contact-stepper__header--counter">
                      <span>1</span>
                    </div>
                    <div className="contact-stepper__header--content">
                      <h1 className="contact-stepper__header--content--title">
                        <span>Titulaire du contrat</span>
                      </h1>
                      <p className="contact-stepper__header--content--description">
                        Informations personnelles de l'adhérent principal
                      </p>
                    </div>
                  </div>
                  <div className="contact-stepper__content">
                    <Input
                      type="radio"
                      variant="group"
                      optional
                      label={"Civilité"}
                      values={[
                        {
                          label: "Monsieur",
                          value: GENDER.MALE,
                        },
                        {
                          label: "Madame",
                          value: GENDER.FEMALE,
                        },
                      ]}
                      icon={<MdOutlineBadge size={22} />}
                      {...bindInputProps({
                        name: "contact.user.gender",
                        values,
                        ...formProps,
                      })}
                    />
                    <Input
                      required
                      icon={<MdOutlineBadge size={22} />}
                      label="Nom"
                      placeholder="Ex : Durant"
                      {...bindTextFieldInputProps({
                        name: "contact.user.lastname",
                        values,
                        setFieldValue,
                        ...formProps,
                      })}
                    />
                    <Input
                      required
                      icon={<MdOutlineBadge size={22} />}
                      label="Prénom"
                      placeholder="Ex : Danielle"
                      {...bindTextFieldInputProps({
                        name: "contact.user.firstname",
                        values,
                        setFieldValue,
                        ...formProps,
                      })}
                    />
                    <Input
                      required
                      icon={<MdOutlineCake size={22} />}
                      label="Date de naissance"
                      placeholder="Ex : 01/01/2020"
                      type="date"
                      variant="simple"
                      defaultPickerValue={dayjs().subtract(26, "years")}
                      format={DATE_FORMAT}
                      {...bindDateInputProps({
                        name: "contact.user.birthDate",
                        values,
                        setFieldValue,
                        ...formProps,
                      })}
                    />
                    <Input
                      required
                      icon={<MdOutlineLocationOn size={22} />}
                      label="Code postal"
                      placeholder="Ex : 69001"
                      {...bindInputProps({
                        name: `contact.telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                        values,
                        ...formProps,
                      })}
                    />
                    <Input
                      required
                      icon={<MdOutlineCall size={16} />}
                      label="Téléphone"
                      placeholder="Ex : 06 12 34 56 78"
                      type="custom"
                      stripSpaces={true}
                      inputMode="numeric"
                      maxLength={10}
                      {...bindInputProps({
                        name: `contact.telecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
                        values,
                        ...formProps,
                      })}
                    />
                    <Input
                      required
                      icon={<MdOutlineEmail size={16} />}
                      label="Email"
                      placeholder="Ex : example@example.com"
                      type="email"
                      {...bindInputProps({
                        name: `contact.telecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
                        values,
                        ...formProps,
                      })}
                    />
                  </div>
                </div>

                <DuplicateCheck
                  key="DUPLICATE"
                  contact={get(values, "contact", {})}
                  onClick={onDuplicateClick}
                />

                <div className="contact-stepper" key="COMPOSITION">
                  <div className="contact-stepper__header">
                    <div className="contact-stepper__header--counter">
                      <span>2</span>
                    </div>
                    <div className="contact-stepper__header--content">
                      <h1 className="contact-stepper__header--content--title">
                        <span>Composition socio-démographique</span>
                      </h1>
                      <p className="contact-stepper__header--content--description">
                        de manière à améliorer votre pouvoir d’achat nous avons
                        quelques questions simples.
                      </p>
                    </div>
                  </div>
                  <div className="contact-stepper__content">
                    <Input
                      type="radio"
                      variant="group"
                      required
                      label={"Quelle est votre Situation Martiale ?"}
                      values={[
                        {
                          label: "Célibataire",
                          value: FAMILY_SITUATION.SINGLE,
                        },
                        {
                          label: "En couple",
                          value: FAMILY_SITUATION.MARRIED,
                        },
                      ]}
                      icon={<MdOutlineBadge size={22} />}
                      {...bindInputProps({
                        name: "contact.familySituation",
                        values,
                        ...formProps,
                      })}
                    />
                    <Select
                      required
                      icon={<MdOutlinePerson size={22} />}
                      label="Dans quel Secteur professionnel êtes vous ?"
                      placeholder="Sélectionner"
                      options={PROFESSIONAL_SECTORES}
                      {...bindInputProps({
                        name: `contact.socioProfessionalCategory`,
                        values,
                        ...formProps,
                      })}
                    />
                    <Select
                      required
                      icon={<MdOutlinePerson size={22} />}
                      label="Quel type de Profession ?"
                      placeholder="Sélectionner"
                      options={PROFESSIONS}
                      {...bindInputProps({
                        name: `contact.fields.profile`,
                        values,
                        ...formProps,
                      })}
                    />
                    <Select
                      allowClear
                      mode="multiple"
                      required
                      icon={<MdOutlinePerson size={22} />}
                      label="Quels sont vos Moyens de locomotion"
                      placeholder="Sélectionner"
                      maxTagCount="responsive"
                      options={MOBILE_MEANS_LIST}
                      {...bindInputProps({
                        name: `contact.fields.mobileMeans`,
                        values,
                        ...formProps,
                      })}
                      onChange={(value) => {
                        setFieldValue(
                          `contact.fields.mobileMeans`,
                          value.filter(Boolean),
                        );
                      }}
                    />
                    <Input
                      type="radio"
                      variant="group"
                      required
                      label={"Au niveau de votre logement vous êtes "}
                      values={[
                        {
                          label: "Locataire",
                          value: PROPERTY_OWNERSHIP.RENTED,
                        },
                        {
                          label: "Propriétaire",
                          value: PROPERTY_OWNERSHIP.OWNED,
                        },
                      ]}
                      icon={<MdOutlineBadge size={22} />}
                      {...bindInputProps({
                        name: "contact.fields.propertyOwnership",
                        values,
                        ...formProps,
                      })}
                    />
                    {get(values, "contact.fields.propertyOwnership") ===
                      PROPERTY_OWNERSHIP.OWNED && (
                      <Input
                        icon={<MdOutlineMoney size={22} />}
                        label="Combien d’année de prêt reste-t-il"
                        type="number"
                        placeholder="Ex : 2"
                        {...bindInputProps({
                          name: `contact.fields.remainingLoanYears`,
                          values,
                          ...formProps,
                        })}
                      />
                    )}
                  </div>
                </div>

                <div className="contact-stepper" key="MEMBERS">
                  <div className="contact-stepper__header">
                    <div className="contact-stepper__header--counter">
                      <span>3</span>
                    </div>
                    <div className="contact-stepper__header--content">
                      <h1 className="contact-stepper__header--content--title">
                        <span>Composition du foyer</span>
                      </h1>
                      <p className="contact-stepper__header--content--description">
                        Ajoutez les membres du foyer pour déterminer la formule
                        optimale
                      </p>
                    </div>
                  </div>
                  <div className="contact-stepper__content">
                    <FieldArray
                      name="members"
                      render={(arrayHelpers) => (
                        <>
                          {values.members.map((member, i) => (
                            <div
                              className="contact-stepper__content--box"
                              key={`member-${member.id}`}
                            >
                              <div className="contact-stepper__content--box--header">
                                <h1 className="contact-stepper__content--box--header--title">
                                  Membre {i + 1}
                                </h1>
                                <div
                                  className="contact-stepper__content--box--header--action"
                                  onClick={() => arrayHelpers.remove(i)}
                                >
                                  <MdOutlineDelete size={14} />
                                  <span>Supprimer</span>
                                </div>
                              </div>
                              <div className="contact-stepper__content--box--content">
                                <Input
                                  required
                                  icon={<MdOutlineBadge size={22} />}
                                  label="Prénom"
                                  placeholder="Prénom"
                                  {...bindTextFieldInputProps({
                                    name: `members.${i}.firstName`,
                                    values,
                                    setFieldValue,
                                    ...formProps,
                                  })}
                                />
                                <Input
                                  required
                                  icon={<MdOutlineBadge size={22} />}
                                  label="Nom"
                                  placeholder="Nom"
                                  {...bindTextFieldInputProps({
                                    name: `members.${i}.lastName`,
                                    values,
                                    setFieldValue,
                                    ...formProps,
                                  })}
                                />
                                <Input
                                  required
                                  icon={<MdOutlineCake size={22} />}
                                  label="Date de naissance"
                                  type="date"
                                  variant="simple"
                                  format={DATE_FORMAT}
                                  {...bindDateInputProps({
                                    name: `members.${i}.birthDate`,
                                    values,
                                    setFieldValue,
                                    ...formProps,
                                  })}
                                />
                                <Select
                                  required
                                  icon={<MdOutlinePerson size={22} />}
                                  label="Lien de parenté"
                                  placeholder="Sélectionner"
                                  options={RELATIONSHIPS}
                                  {...bindInputProps({
                                    name: `members.${i}.relationship`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                              </div>
                            </div>
                          ))}
                          <button
                            key="add-member"
                            className="button__action"
                            onClick={() =>
                              arrayHelpers.push({
                                id: cuid(),
                                firstName: "",
                                lastName: "",
                                birthDate: null,
                                relationship: "",
                              })
                            }
                          >
                            <MdAdd size={22} />
                            Ajouter un membre
                          </button>
                        </>
                      )}
                    />
                  </div>
                </div>

                <div className="quote--recommendation">
                  <span className="quote--recommendation--title">
                    Recommandation de formule
                  </span>
                  <p className="quote--recommendation--description">
                    <b>Formule {recommendation.formula} recommandée</b> {" - "}
                    <span>{recommendation.text}</span>
                  </p>
                </div>
              </Form>
            </div>
            <ActionButtons
              onSubmit={handleSubmit}
              loading={nextLoading}
              disabled={{
                next: nextLoading || !isValid,
              }}
              translation={{
                NEXT: "Continuer",
              }}
            />
          </div>
        );
      }}
    </Formik>
  );
};

export default View;
